'use client';

import { useState, useEffect } from 'react';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { HealthVoxMark } from '@/components/layout/healthvox-mark';
import Link from 'next/link';
import {
  Mail, Lock, Eye, EyeOff, ArrowRight, ArrowLeft,
  Loader2, User, Calendar, Phone, ChevronDown, Stethoscope, CheckCircle2,
} from 'lucide-react';
import { eventLogger } from '@/services/auth';
import { setUser, track } from '@inleads/event-logger';
import { getProviders, type PublicProvider } from '@/lib/api/parse/public';

// ── Types ────────────────────────────────────────────────────────────────────
type Step = 1 | 2;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PASSWORD_RULES = [
  { label: '8+ characters',   test: (p: string) => p.length >= 8 },
  { label: 'Uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'Lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { label: 'Number',           test: (p: string) => /[0-9]/.test(p) },
  { label: 'Special character',test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

const COUNTRY_CODES = [
  { code: '+91',  label: '+91 (IN)' },
  { code: '+1',   label: '+1 (US/CA)' },
  { code: '+44',  label: '+44 (UK)' },
  { code: '+61',  label: '+61 (AU)' },
  { code: '+49',  label: '+49 (DE)' },
  { code: '+33',  label: '+33 (FR)' },
  { code: '+81',  label: '+81 (JP)' },
  { code: '+971', label: '+971 (AE)' },
];

// ── Field wrappers ───────────────────────────────────────────────────────────
function Field({
  icon: Icon, type, placeholder, value, onChange, onToggle, showToggle, error, disabled, autoFocus, autoComplete,
}: {
  icon: React.ElementType;
  type: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  onToggle?: () => void;
  showToggle?: boolean;
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
}) {
  return (
    <div>
      <div className={`flex h-10 w-full rounded-xl border bg-slate-50 overflow-hidden transition-all ${
        error
          ? 'border-red-400 ring-1 ring-red-300'
          : 'border-slate-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-200'
      }`}>
        <span className="flex items-center pl-3 pr-2 flex-shrink-0">
          <Icon className={`h-3.5 w-3.5 ${error ? 'text-red-400' : 'text-slate-400'}`} />
        </span>
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          className="flex-1 h-full bg-transparent text-sm text-slate-900 focus:outline-none placeholder:text-slate-400 disabled:opacity-60 min-w-0"
        />
        {onToggle && (
          <button type="button" onClick={onToggle} tabIndex={-1}
            className="px-3 text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
          >
            {showToggle ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
      {error && <p className="text-xs text-red-500 mt-1 pl-1">{error}</p>}
    </div>
  );
}

// ── Step dots ────────────────────────────────────────────────────────────────
function StepDots({ step }: { step: Step }) {
  return (
    <div className="flex items-center gap-2 justify-center mb-4">
      {([1, 2] as Step[]).map((s) => (
        <div key={s} className={`rounded-full transition-all duration-300 ${
          s === step
            ? 'w-6 h-2 bg-indigo-500'
            : s < step
            ? 'w-2 h-2 bg-indigo-400'
            : 'w-2 h-2 bg-slate-200'
        }`} />
      ))}
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function PatientSignupPage() {
  const router = useRouter();

  const [step, setStep]     = useState<Step>(1);
  const [mounted, setMounted] = useState(false);

  // Step 1
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [confirm, setConfirm]       = useState('');
  const [showPass, setShowPass]     = useState(false);
  const [showConf, setShowConf]     = useState(false);

  // Step 2
  const [firstName, setFirstName]   = useState('');
  const [lastName, setLastName]     = useState('');
  const [dob, setDob]               = useState('');
  const [gender, setGender]         = useState('');
  const [phone, setPhone]           = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [providerPickerOpen, setProviderPickerOpen] = useState(false);

  // Providers
  const [providers, setProviders]         = useState<PublicProvider[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);

  // UI
  const [isLoading, setIsLoading]   = useState(false);
  const [errors, setErrors]         = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState('');

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    setLoadingProviders(true);
    getProviders()
      .then((list) => {
        setProviders(list);
        if (list.length > 0) setSelectedProviderId(list[0].objectId);
      })
      .catch(() => {})
      .finally(() => setLoadingProviders(false));
  }, []);

  const passwordMeta = PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(password) }));
  const passwordStrong = passwordMeta.every((r) => r.ok);

  // ── Step 1 submit ──────────────────────────────────────────────────────────
  const handleStep1 = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!email.trim()) errs.email = 'Email is required.';
    else if (!EMAIL_RE.test(email.trim())) errs.email = 'Enter a valid email address.';
    if (!password) errs.password = 'Password is required.';
    else if (!passwordStrong) errs.password = 'Password does not meet requirements.';
    if (!confirm) errs.confirm = 'Please confirm your password.';
    else if (confirm !== password) errs.confirm = 'Passwords do not match.';
    setErrors(errs);
    if (Object.keys(errs).length === 0) setStep(2);
  };

  // ── Step 2 submit ──────────────────────────────────────────────────────────
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (firstName.trim().length < 2) errs.firstName = 'Min 2 characters.';
    if (lastName.trim().length < 2)  errs.lastName  = 'Min 2 characters.';
    if (!dob)    errs.dob    = 'Date of birth is required.';
    if (!gender) errs.gender = 'Please select a gender.';
    if (!phone.trim()) errs.phone = 'Phone number is required.';
    else if (!/^\d{10}$/.test(phone.trim())) errs.phone = 'Enter a valid 10-digit phone number.';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setIsLoading(true);
    setGeneralError('');
    try {
      const res = await fetch('/api/parse/cloud', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          functionName: 'patientSignup',
          params: {
            email: email.trim().toLowerCase(),
            password,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            dateOfBirth: dob,
            gender,
            phoneNumber: phone || undefined,
            countryCode: phone ? countryCode : undefined,
            providerId: selectedProviderId || undefined,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Signup failed. Please try again.');

      // Auto-login after signup
      const loginResult = await signIn('patient-credentials', {
        email: email.trim().toLowerCase(),
        password,
        redirect: false,
      });

      if (loginResult?.ok) {
        eventLogger();
        await setUser(email.trim().toLowerCase(), `${firstName.trim()} ${lastName.trim()}`);
        await track('SIGNUP');
        router.push('/patient/dashboard');
        router.refresh();
      } else {
        router.push('/login/patient');
      }
    } catch (err: any) {
      setGeneralError(err.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = (key: string) => setErrors((e) => { const n = { ...e }; delete n[key]; return n; });

  const validateDob = (value: string) => {
    if (!value) { clearError('dob'); return; }
    const d = new Date(value + 'T00:00:00');
    if (isNaN(d.getTime()) || d > new Date()) {
      setErrors((e) => ({ ...e, dob: 'Enter a valid date of birth.' }));
      return;
    }
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    if (d > twoYearsAgo) {
      setErrors((e) => ({ ...e, dob: 'Patient must be at least 2 years old.' }));
      return;
    }
    clearError('dob');
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="h-[100dvh] overflow-hidden flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-indigo-50/60 to-blue-50 px-4">
      <div className={`w-full max-w-sm transition-all duration-700 ease-out ${
        mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}>

        {/* ── Branding ── */}
        <div className="text-center mb-4">
          <HealthVoxMark className="inline-block h-14 w-14 mb-3 text-indigo-500 drop-shadow-lg" />
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">Create Account</h1>
          <p className="text-xs text-slate-500 mt-0.5">Join HealthVox today</p>
        </div>

        {/* ── Card ── */}
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100">

          <div className="px-5 pt-5 pb-1">
            <StepDots step={step} />
          </div>

          {/* ════ Step 1: Account details ════ */}
          {step === 1 && (
            <form onSubmit={handleStep1} className="px-5 pb-5 space-y-3" noValidate>
              <div>
                <h2 className="text-sm font-semibold text-slate-800">Account Details</h2>
                <p className="text-xs text-slate-500 mt-0.5">Step 1 of 2 — your login credentials</p>
              </div>

              {/* Email */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-700">Email</label>
                <Field
                  icon={Mail}
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(v) => { setEmail(v); clearError('email'); setGeneralError(''); }}
                  error={errors.email}
                  disabled={isLoading}
                  autoFocus
                  autoComplete="email"
                />
              </div>

              {/* Password */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-700">Password</label>
                <Field
                  icon={Lock}
                  type={showPass ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(v) => {
                    setPassword(v);
                    if (v && !PASSWORD_RULES.every((r) => r.test(v))) {
                      setErrors((e) => ({ ...e, password: 'Must have 8+ chars, uppercase, lowercase, number & special character.' }));
                    } else {
                      clearError('password');
                    }
                  }}
                  onToggle={() => setShowPass(v => !v)}
                  showToggle={showPass}
                  error={errors.password}
                  disabled={isLoading}
                  autoComplete="new-password"
                />
              </div>

              {/* Confirm password */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-700">Confirm Password</label>
                <Field
                  icon={Lock}
                  type={showConf ? 'text' : 'password'}
                  placeholder="Repeat your password"
                  value={confirm}
                  onChange={(v) => { setConfirm(v); clearError('confirm'); }}
                  onToggle={() => setShowConf(v => !v)}
                  showToggle={showConf}
                  error={errors.confirm}
                  disabled={isLoading}
                  autoComplete="new-password"
                />
              </div>

              <button
                type="submit"
                className="w-full h-10 rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white text-sm font-semibold flex items-center justify-center gap-2 shadow-md shadow-indigo-500/25 transition-all active:scale-[0.98] mt-1"
              >
                Continue <ArrowRight className="h-4 w-4" />
              </button>

              <p className="text-center text-xs text-slate-500 pt-1">
                Already have an account?{' '}
                <Link href="/login/patient" className="text-indigo-600 font-semibold hover:underline">
                  Sign in
                </Link>
              </p>
            </form>
          )}

          {/* ════ Step 2: Personal info ════ */}
          {step === 2 && (
            <form onSubmit={handleSignup} className="px-5 pb-5 space-y-3" noValidate>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { setStep(1); setGeneralError(''); }}
                  className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors flex-shrink-0"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <div>
                  <h2 className="text-sm font-semibold text-slate-800">Personal Details</h2>
                  <p className="text-xs text-slate-500">Step 2 of 2 — a little about you</p>
                </div>
              </div>

              {/* General error */}
              {generalError && (
                <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-3 py-2">
                  <svg className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                  </svg>
                  <p className="text-xs text-red-600">{generalError}</p>
                </div>
              )}

              {/* First + Last name */}
              <div className="grid grid-cols-2 gap-2.5">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-700">First Name</label>
                  <Field
                    icon={User}
                    type="text"
                    placeholder="John"
                    value={firstName}
                    onChange={(v) => { setFirstName(v); clearError('firstName'); }}
                    error={errors.firstName}
                    disabled={isLoading}
                    autoFocus
                    autoComplete="given-name"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-700">Last Name</label>
                  <Field
                    icon={User}
                    type="text"
                    placeholder="Smith"
                    value={lastName}
                    onChange={(v) => { setLastName(v); clearError('lastName'); }}
                    error={errors.lastName}
                    disabled={isLoading}
                    autoComplete="family-name"
                  />
                </div>
              </div>

              {/* DOB */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-700">Date of Birth</label>
                <div className={`flex h-10 w-full rounded-xl border bg-slate-50 overflow-hidden transition-all ${
                  errors.dob
                    ? 'border-red-400 ring-1 ring-red-300'
                    : 'border-slate-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-200'
                }`}>
                  <span className="flex items-center pl-3 pr-2 flex-shrink-0">
                    <Calendar className={`h-3.5 w-3.5 ${errors.dob ? 'text-red-400' : 'text-slate-400'}`} />
                  </span>
                  <input
                    type="date"
                    value={dob}
                    onChange={(e) => { setDob(e.target.value); validateDob(e.target.value); }}
                    disabled={isLoading}
                    max={new Date().toISOString().split('T')[0]}
                    className="flex-1 h-full pr-3 bg-transparent text-sm text-slate-900 focus:outline-none disabled:opacity-60 min-w-0"
                  />
                </div>
                {errors.dob && <p className="text-xs text-red-500 pl-1">{errors.dob}</p>}
              </div>

              {/* Gender */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-700">Gender</label>
                <div className="grid grid-cols-3 gap-2">
                  {['Male', 'Female', 'Other'].map((g) => (
                    <button
                      key={g} type="button"
                      onClick={() => { setGender(g); clearError('gender'); }}
                      disabled={isLoading}
                      className={`h-9 rounded-xl text-xs font-semibold border-2 transition-all ${
                        gender === g
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
                {errors.gender && <p className="text-xs text-red-500 pl-1">{errors.gender}</p>}
              </div>

              {/* Phone */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-700">Phone *</label>
                <div className={`flex h-10 w-full rounded-xl border bg-slate-50 overflow-hidden transition-all ${errors.phone ? 'border-red-400 ring-1 ring-red-300' : 'border-slate-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-200'}`}>
                  {/* Country code */}
                  <div className="relative flex items-center border-r border-slate-200 bg-slate-100 shrink-0 pl-2.5 pr-1.5">
                    <Phone className="h-3 w-3 text-slate-400 mr-1 flex-shrink-0" />
                    <span className="text-xs font-semibold text-slate-700 pointer-events-none select-none">
                      {countryCode}
                    </span>
                    <ChevronDown className="h-3 w-3 ml-0.5 text-slate-400 pointer-events-none flex-shrink-0" />
                    <select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      disabled={isLoading}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      aria-label="Country code"
                    >
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <input
                    type="tel"
                    inputMode="numeric"
                    placeholder="10-digit number"
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); clearError('phone'); }}
                    disabled={isLoading}
                    className="flex-1 h-full px-3 bg-transparent text-sm text-slate-900 focus:outline-none placeholder:text-slate-400 disabled:opacity-60 min-w-0"
                  />
                </div>
                {errors.phone && <p className="text-xs text-red-500 mt-1 pl-1">{errors.phone}</p>}
              </div>

              {/* Healthcare Provider (optional) */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-700">
                  Healthcare Provider <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <button
                  type="button"
                  onClick={() => setProviderPickerOpen(true)}
                  disabled={isLoading || loadingProviders}
                  className="flex h-10 w-full items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm transition-all hover:border-indigo-400 disabled:opacity-60"
                >
                  <Stethoscope className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                  <span className={`flex-1 text-left truncate ${selectedProviderId ? 'text-slate-900' : 'text-slate-400'}`}>
                    {loadingProviders ? 'Loading providers…' : (providers.find(p => p.objectId === selectedProviderId)?.name ?? 'Select Healthcare Provider')}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                </button>
              </div>

              {/* Provider picker modal */}
              {providerPickerOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setProviderPickerOpen(false)}>
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                      <p className="text-sm font-semibold text-slate-800">Healthcare Provider</p>
                      <button type="button" onClick={() => setProviderPickerOpen(false)} className="text-slate-400 hover:text-slate-600">✕</button>
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {providers.length === 0 ? (
                        <p className="text-center text-sm text-slate-400 py-8">No providers available</p>
                      ) : providers.map((p) => (
                        <button
                          key={p.objectId}
                          type="button"
                          onClick={() => { setSelectedProviderId(p.objectId); setProviderPickerOpen(false); }}
                          className={`flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-slate-50 border-b border-slate-50 last:border-0 ${selectedProviderId === p.objectId ? 'bg-indigo-50' : ''}`}
                        >
                          <div className="h-9 w-9 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                            <Stethoscope className="h-4 w-4 text-indigo-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-800 truncate">{p.name}</p>
                            {(p.city || p.state) && (
                              <p className="text-xs text-slate-400 truncate">{[p.city, p.state].filter(Boolean).join(', ')}</p>
                            )}
                          </div>
                          {selectedProviderId === p.objectId && <CheckCircle2 className="h-4 w-4 text-indigo-500 flex-shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-10 rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white text-sm font-semibold flex items-center justify-center gap-2 shadow-md shadow-indigo-500/25 transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed disabled:shadow-none mt-1"
              >
                {isLoading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" />Creating account…</>
                ) : (
                  <>Create Account <ArrowRight className="h-4 w-4" /></>
                )}
              </button>

              <p className="text-center text-xs text-slate-500 pt-1">
                Already have an account?{' '}
                <Link href="/login/patient" className="text-indigo-600 font-semibold hover:underline">
                  Sign in
                </Link>
              </p>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-[11px] text-slate-400 mt-4">
          By continuing you agree to our{' '}
          <span className="underline cursor-pointer hover:text-slate-600">Terms</span>
          {' '}&amp;{' '}
          <span className="underline cursor-pointer hover:text-slate-600">Privacy Policy</span>
        </p>
      </div>
    </div>
  );
}
